import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { isStockOrETF, yahooTicker } from '../utils/stocks';
import { convertToUsd } from './useExchangeRates';

// A richer sibling of useStockPrices: it makes the SAME /api/stock-quote calls but
// retains more of Yahoo's chart meta (the 52-week range) for the Market → Stocks &
// ETFs table. Prices AND the 52-week low/high are converted to USD (Yahoo reports in
// the ticker's native currency — AUD for VAS.AX/STW.AX), consistent with the price
// path, so nothing is ever shown as USD when it's really AUD. No new endpoint.

const CACHE_KEY = 'mycryptoplanner_stock_market';
const CACHE_TTL = 60_000;

export interface StockMarketEntry {
  usd: number;                   // price in USD (0 = unavailable)
  usd_24h_change: number;        // 24h % change (currency-invariant)
  week52LowUsd: number | null;   // 52-week low in USD (null = unavailable)
  week52HighUsd: number | null;  // 52-week high in USD (null = unavailable)
}

interface CacheEntry extends StockMarketEntry { fetchedAt: number; }
type Cache = Record<string, CacheEntry>; // keyed by Yahoo ticker (e.g. "AAPL", "VAS.AX")

/** assetId → market entry, e.g. { 'stock:AAPL': { usd, usd_24h_change, … } } */
export type StockMarketMap = Record<string, StockMarketEntry>;

function loadCache(): Cache {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Cache) : {};
  } catch { return {}; }
}

function saveCache(cache: Cache) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch { /* quota */ }
}

/** Fetch one ticker's price + 24h change + 52-week range via the Yahoo proxy,
 *  all converted to USD. Throws (per-ticker isolated by the caller) if the quote
 *  lacks a currency or we have no FX rate — never returns a mis-denominated value. */
async function fetchStockQuoteFull(ticker: string): Promise<StockMarketEntry> {
  const { data } = await axios.get('/api/stock-quote', {
    params: { ticker },
    timeout: 10_000,
    headers: { Accept: 'application/json' },
  });
  const meta = data?.chart?.result?.[0]?.meta ?? {};

  const currency = typeof meta.currency === 'string' ? meta.currency.toUpperCase() : '';
  if (!currency) throw new Error(`Quote for ${ticker} has no currency.`);

  const rawPrice = (meta.regularMarketPrice ?? 0) as number;
  const rawPrev  = (meta.chartPreviousClose ?? meta.previousClose ?? rawPrice) as number;

  const usd = convertToUsd(rawPrice, currency);
  if (usd === null) throw new Error(`No FX rate to convert ${currency} to USD for ${ticker}.`);
  const prevUsd = convertToUsd(rawPrev, currency) ?? usd;
  const usd_24h_change = prevUsd > 0 ? ((usd - prevUsd) / prevUsd) * 100 : 0;

  const rawLow  = typeof meta.fiftyTwoWeekLow  === 'number' ? meta.fiftyTwoWeekLow  : null;
  const rawHigh = typeof meta.fiftyTwoWeekHigh === 'number' ? meta.fiftyTwoWeekHigh : null;
  const week52LowUsd  = rawLow  !== null ? convertToUsd(rawLow,  currency) : null;
  const week52HighUsd = rawHigh !== null ? convertToUsd(rawHigh, currency) : null;

  return { usd, usd_24h_change, week52LowUsd, week52HighUsd };
}

function cacheToMap(cache: Cache, assetIds: string[]): StockMarketMap {
  const map: StockMarketMap = {};
  for (const id of assetIds) {
    if (!isStockOrETF(id)) continue;
    const entry = cache[yahooTicker(id)];
    if (entry) {
      map[id] = {
        usd: entry.usd,
        usd_24h_change: entry.usd_24h_change,
        week52LowUsd: entry.week52LowUsd,
        week52HighUsd: entry.week52HighUsd,
      };
    }
  }
  return map;
}

// ── Batch variant (one /api/stock-quotes call for a whole market page) ──────────
// The Market → Stocks & ETFs table now paginates over a few hundred tickers. Fanning
// out one /api/stock-quote per ticker (useStockMarket, above) would be ~100 upstream
// requests per page; this fetches the visible page in ONE /api/stock-quotes call and
// shares the same localStorage cache + StockMarketEntry shape. useStockMarket is kept
// for any single-asset callers; new paginated table uses this.

interface BatchQuote {
  symbol: string;
  currency: string | null;
  price: number | null;
  prevClose: number | null;
  changePercent: number | null;
  low52: number | null;
  high52: number | null;
  shortName: string;
}

/** Normalise one batch quote → USD-denominated entry, or null if it can't be
 *  converted (missing currency/price, or no FX rate) — never a mis-denominated value. */
function quoteToEntry(q: BatchQuote): StockMarketEntry | null {
  const currency = typeof q.currency === 'string' ? q.currency.toUpperCase() : '';
  if (!currency || typeof q.price !== 'number') return null;

  const usd = convertToUsd(q.price, currency);
  if (usd === null) return null;

  const prevUsd = typeof q.prevClose === 'number' ? convertToUsd(q.prevClose, currency) : null;
  const usd_24h_change =
    prevUsd !== null && prevUsd > 0
      ? ((usd - prevUsd) / prevUsd) * 100
      : typeof q.changePercent === 'number'
      ? q.changePercent // percent is currency-invariant — safe fallback
      : 0;

  const week52LowUsd  = typeof q.low52  === 'number' ? convertToUsd(q.low52,  currency) : null;
  const week52HighUsd = typeof q.high52 === 'number' ? convertToUsd(q.high52, currency) : null;

  return { usd, usd_24h_change, week52LowUsd, week52HighUsd };
}

/** Batch market data for exactly the given (page's) assetIds. One upstream request
 *  covers up to 120 tickers, so a 100-row page = a single /api/stock-quotes call. */
export function useStockMarketBatch(assetIds: string[]) {
  const stockIds   = assetIds.filter(isStockOrETF);
  const tickers    = stockIds.map(yahooTicker);
  const tickersKey = [...tickers].sort().join(',');

  const [data,        setData]        = useState<StockMarketMap>(() => cacheToMap(loadCache(), assetIds));
  const [loading,     setLoading]     = useState(stockIds.length > 0);
  const [error,       setError]       = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchPage = useCallback(async (force = false) => {
    if (tickers.length === 0) { setLoading(false); return; }

    const cache = loadCache();
    const now   = Date.now();
    const allFresh = tickers.every((t) => cache[t] && now - cache[t].fetchedAt < CACHE_TTL);

    if (!force && allFresh) {
      setData(cacheToMap(cache, stockIds));
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const { data: resp } = await axios.get('/api/stock-quotes', {
        params: { symbols: tickers.join(',') },
        timeout: 12_000,
        headers: { Accept: 'application/json' },
      });
      const quotes: BatchQuote[] = Array.isArray(resp?.quotes) ? resp.quotes : [];

      const newCache: Cache = { ...loadCache() };
      let converted = 0;
      for (const q of quotes) {
        const entry = quoteToEntry(q);
        if (entry) { newCache[q.symbol] = { ...entry, fetchedAt: now }; converted++; }
      }
      saveCache(newCache);
      setData(cacheToMap(newCache, stockIds));
      setLastUpdated(new Date());
      if (converted === 0) setError('Unable to fetch live stock prices.');
    } catch {
      setData(cacheToMap(loadCache(), stockIds));
      setError('Unable to fetch live stock prices.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickersKey]);

  useEffect(() => {
    setLoading(true);
    fetchPage(false);
    const id = setInterval(() => fetchPage(true), CACHE_TTL);
    return () => clearInterval(id);
  }, [tickersKey, fetchPage]);

  return { data, loading, error, lastUpdated, refresh: () => fetchPage(true) };
}

export function useStockMarket(assetIds: string[]) {
  const stockIds   = assetIds.filter(isStockOrETF);
  const tickers    = stockIds.map(yahooTicker);
  const tickersKey = [...tickers].sort().join(',');

  const [data,    setData]    = useState<StockMarketMap>(() => cacheToMap(loadCache(), assetIds));
  const [loading, setLoading] = useState(stockIds.length > 0);
  const [error,   setError]   = useState<string | null>(null);

  const fetchAll = useCallback(async (force = false) => {
    if (tickers.length === 0) { setLoading(false); return; }

    const cache = loadCache();
    const now   = Date.now();
    const allFresh = tickers.every((t) => cache[t] && now - cache[t].fetchedAt < CACHE_TTL);

    if (!force && allFresh) {
      setData(cacheToMap(cache, stockIds));
      setLoading(false);
      return;
    }

    try {
      setError(null);
      // Fan out in parallel; settle individually so one bad ticker doesn't block the rest.
      const results = await Promise.allSettled(
        tickers.map(async (ticker) => ({ ticker, ...(await fetchStockQuoteFull(ticker)) }))
      );

      const newCache: Cache = { ...loadCache() };
      for (const r of results) {
        if (r.status === 'fulfilled') {
          const { ticker, ...entry } = r.value;
          newCache[ticker] = { ...entry, fetchedAt: now };
        }
      }
      saveCache(newCache);
      setData(cacheToMap(newCache, stockIds));
    } catch {
      setData(cacheToMap(loadCache(), stockIds));
      setError('Unable to fetch live stock prices.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickersKey]);

  useEffect(() => {
    fetchAll(false);
    const id = setInterval(() => fetchAll(true), CACHE_TTL);
    return () => clearInterval(id);
  }, [tickersKey, fetchAll]);

  return { data, loading, error, refresh: () => fetchAll(true) };
}
